/**
 * The documents panel (F5b) — presentational.
 *
 * Takes its rows and its handlers as props and fetches nothing. That is what
 * lets it be shared: plan §3.1 forbids a feature importing another feature,
 * and `components/domain` importing a feature's api/ layer, so a panel that
 * called the attachments query could live in exactly one feature. Each
 * feature keeps a small container that supplies the data instead — see
 * `features/startups/components/StartupDocuments.tsx`.
 *
 * **PRD §5.10's statement leads.** The labels are on display here, so this is
 * where someone forms the belief that CONFIDENTIAL means restricted — and the
 * requirement is that the interface corrects that belief rather than trading
 * on it.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.8, PRD §5.10
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AttachmentSummary } from '@nexus/contracts';

import { formatDate, formatFileSize, formatNumber } from '../../lib/format';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card, CardBody, CardHead } from '../ui/Card';
import type { Column, SortState } from '../ui/data-table-types';
import { DataTable } from '../ui/DataTable';
import { Dialog } from '../ui/Dialog';
import { Empty } from '../ui/Empty';
import { Kicker } from '../ui/Kicker';

import { ConfidentialityBadge } from './ConfidentialityBadge';
import { LabelOnlyNotice } from './LabelOnlyNotice';

export interface AttachmentsPanelProps {
  attachments: readonly AttachmentSummary[];
  total: number;
  isPending: boolean;
  isError: boolean;
  /** Whether the viewer may upload (EDITOR) and delete (ADMIN). */
  canUpload: boolean;
  canDelete: boolean;
  /** Which row's download is being prepared, if any. */
  downloadingId?: string | undefined;
  isDeleting?: boolean;
  onUpload: () => void;
  onDownload: (attachment: AttachmentSummary) => void;
  onDelete: (attachment: AttachmentSummary) => void;
  /** A failure to report above the list. */
  error?: string | undefined;
  /** Sorting is server-side, so the panel reports the click and shows state. */
  sort: SortState | null;
  onSortChange: (field: string) => void;
}

export function AttachmentsPanel({
  attachments,
  total,
  isPending,
  isError,
  canUpload,
  canDelete,
  downloadingId,
  isDeleting = false,
  onUpload,
  onDownload,
  onDelete,
  error,
  sort,
  onSortChange,
}: AttachmentsPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState<AttachmentSummary | null>(null);

  const columns: readonly Column<AttachmentSummary>[] = [
    {
      key: 'name',
      header: t('attachment.columns.name'),
      cell: (row) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-navy-text">{row.fileName}</span>
          <ConfidentialityBadge confidentiality={row.confidentiality} />
        </span>
      ),
      sortField: 'fileName',
      card: 'primary',
    },
    {
      key: 'type',
      header: t('attachment.columns.type'),
      cell: (row) => <Badge tone="mute">{t(`attachment.type.${row.type}`)}</Badge>,
      sortField: 'type',
      card: 'secondary',
    },
    {
      key: 'size',
      header: t('attachment.columns.size'),
      cell: (row) => (
        <span className="tabular-nums">{formatFileSize(row.sizeBytes)}</span>
      ),
      sortField: 'sizeBytes',
      numeric: true,
      card: 'secondary',
    },
    {
      key: 'uploadedBy',
      header: t('attachment.columns.uploadedBy'),
      /* Null only between a user being deleted and this row being read — the
         column is nullable so `SetNull` can fire, not because uploads are
         anonymous. */
      cell: (row) => row.uploadedByName ?? t('attachment.columns.unknownUploader'),
      card: 'hidden',
    },
    {
      key: 'uploaded',
      header: t('attachment.columns.uploaded'),
      /* `whitespace-nowrap`: "2 Sept 2026" wrapped onto two lines in this
         column, which made the row taller than every other and looked like a
         rendering fault. */
      cell: (row) => (
        <span className="tabular-nums whitespace-nowrap">
          {formatDate(row.createdAt)}
        </span>
      ),
      sortField: 'createdAt',
      numeric: true,
      card: 'hidden',
    },
    {
      key: 'downloads',
      header: t('attachment.columns.downloads'),
      /* PRD §5.10 logs every download; the count is what makes that visible
         without opening the log. "Never opened" rather than a zero, because a
         document nobody has read is a fact worth noticing. */
      /**
       * Labelled on the card layout.
       *
       * `DataTable` lays its secondary cells out in one wrapping row with
       * only a gap between them, so a bare count read as "PITCH DECK 1 KB 2"
       * in a real browser — a number with nothing saying what it counts. The
       * table's column header does that job; a card has no header, so the
       * cell carries it. "Never opened" already says what it is.
       */
      cell: (row) =>
        row.downloadCount === 0 ? (
          <span className="text-gray-400 whitespace-nowrap">
            {t('attachment.columns.never')}
          </span>
        ) : (
          <span className="tabular-nums">
            {t('attachment.columns.downloadCount', { count: row.downloadCount })}
          </span>
        ),
      numeric: true,
      card: 'secondary',
    },
    {
      key: 'actions',
      header: t('attachment.columns.actions'),
      cell: (row) => (
        <span className="flex justify-end gap-2">
          <Button
            variant="link"
            size="sm"
            disabled={downloadingId !== undefined}
            onClick={() => {
              onDownload(row);
            }}
          >
            {downloadingId === row.id
              ? t('attachment.actions.downloading')
              : t('attachment.actions.download')}
          </Button>
          {canDelete ? (
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setPendingDelete(row);
              }}
            >
              {t('attachment.actions.delete')}
            </Button>
          ) : null}
        </span>
      ),
      numeric: true,
      card: 'secondary',
    },
  ];

  if (isError) {
    return (
      <Card>
        <CardBody>
          <p role="alert" className="text-sem-bad text-sm">
            {t('errors.generic')}
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHead>
          <Kicker as="h2">
            {t('attachment.title')}
            {isPending ? null : ` · ${formatNumber(total)}`}
          </Kicker>
          {canUpload ? (
            <Button size="sm" className="ml-s2" onClick={onUpload}>
              {t('attachment.actions.upload')}
            </Button>
          ) : null}
        </CardHead>

        <CardBody>
          {/* Above the list, where the labels are visible. Someone reading a
              row marked CONFIDENTIAL concludes it is restricted; PRD §5.10
              requires the interface to say plainly that it is not. */}
          <LabelOnlyNotice className="mb-s3" />

          {error === undefined ? null : (
            <p role="alert" className="text-sem-bad mb-s3 text-sm">
              {error}
            </p>
          )}

          {!isPending && attachments.length === 0 ? (
            <Empty title={t('attachment.empty')}>{t('attachment.emptyHint')}</Empty>
          ) : (
            <DataTable
              rows={attachments}
              columns={columns}
              rowKey={(row) => row.id}
              caption={t('attachment.caption')}
              isLoading={isPending}
              sort={sort}
              onSortChange={onSortChange}
              emptyMessage={t('attachment.empty')}
            />
          )}
        </CardBody>
      </Card>

      {/* Named in the confirmation, and explicit that the download log
          survives — PRD §6.2 keeps records so a deletion can be explained. */}
      {/* Rendered only when there IS a pending row, rather than always-mounted
          with `open={pendingDelete !== null}`. That removes a
          `pendingDelete === null` guard inside the confirm handler which
          nothing could ever reach — the dialog was only openable with a row
          set — and an unreachable guard is a branch no test can cover. */}
      {pendingDelete === null ? null : (
        <Dialog
          open
          /* No `if (!next)` guard: the dialog is mounted already-open and
             only unmounts when the row is cleared, so Radix can only ever
             report a close here — and guarding for an open it cannot send
             would be a branch no test could reach. */
          onOpenChange={() => {
            setPendingDelete(null);
          }}
          title={t('attachment.delete.title')}
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setPendingDelete(null);
                }}
              >
                {t('attachment.delete.cancel')}
              </Button>
              <Button
                variant="danger"
                disabled={isDeleting}
                onClick={() => {
                  onDelete(pendingDelete);
                  setPendingDelete(null);
                }}
              >
                {t('attachment.delete.confirm')}
              </Button>
            </>
          }
        >
          <p className="text-body text-sm">
            {t('attachment.delete.body', { fileName: pendingDelete.fileName })}
          </p>
        </Dialog>
      )}
    </>
  );
}
